import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { PageHeader, SectionDivider } from "@/components/primitives";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ShieldCheck, Users, Mail, Flower2, ChevronRight, Clock, CheckCircle2 } from "lucide-react";

/**
 * The Heritage Office — admin only.
 * Strictly separate from the personal Garden/Beds/Stats.
 * Three things only: who is using it, what they need answered, what to release.
 */
export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user?.is_admin) api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [user]);

  if (user === null) return null;
  if (!user || !user.is_admin) return <Navigate to="/admin/login" replace />;

  return (
    <div data-testid="admin-page">
      <PageHeader
        kicker="Heritage Office"
        title="The Office"
        subtitle="Run the patch · separate from your own garden"
      />

      <SectionDivider label="People in the patch" />

      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4" data-testid="admin-people-tiles">
        <DrillTile to="/admin/users" label="Gardeners" value={stats?.users} testid="admin-stat-users" />
        <DrillTile to="/admin/users" label="Total plants" value={stats?.plants} testid="admin-stat-plants" />
        <DrillTile to="/admin/users" label="Total beds" value={stats?.beds} testid="admin-stat-beds" />
        <DrillTile to="/admin/community" label="Total blooms" value={stats?.community_blooms} testid="admin-stat-blooms" />
      </section>

      <SectionDivider label="What's waiting for you" className="mt-10" />

      <section className="grid sm:grid-cols-2 gap-3 mt-4" data-testid="admin-waiting">
        <WaitingCard
          to="/admin/community"
          icon={Clock}
          accent
          count={stats?.community_pending}
          title="Blooms to release"
          blurb="Submissions sit in The Vault until you say so. Open it, tap Release, and they appear on the public flat lay."
          testid="admin-card-pending"
        />
        <WaitingCard
          to="/admin/contact"
          icon={Mail}
          count={stats?.contact_messages}
          title="Letters from gardeners"
          blurb="Notes posted via the Contact form. Tap a letter to read it; one tap to reply by email."
          testid="admin-card-contacts"
        />
      </section>

      <SectionDivider label="Tools" className="mt-10" />

      <section className="grid sm:grid-cols-3 gap-3 mt-4" data-testid="admin-tools">
        <ToolLink to="/admin/users"     icon={Users}     label="Gardeners"   blurb="Names, emails, gardens, and who's most active."     testid="admin-tool-users" />
        <ToolLink to="/admin/community" icon={Flower2}   label="The Vault"   blurb="Curate the community flat lay. Release or hide."     testid="admin-tool-community" />
        <ToolLink to="/admin/contact"   icon={Mail}      label="Letters"     blurb="Inbox for the Contact form. Reply via email."        testid="admin-tool-contacts" />
      </section>

      <p className="text-center font-display italic text-ink-soft mt-12">
        Your own patch lives in <Link to="/home" className="underline text-sage-dark">Home</Link>. The Office is just for keeping the doors open.
      </p>
    </div>
  );
}

function DrillTile({ to, label, value, testid }) {
  return (
    <Link to={to} className="h-card p-4 hover:shadow-seal transition-shadow block" data-testid={testid}>
      <div className="font-display text-3xl tabular-nums text-sage-dark">{value ?? "—"}</div>
      <div className="kicker mt-1 text-[10px] flex items-center justify-between">
        <span>{label}</span>
        <ChevronRight size={12} className="text-sage-dark" />
      </div>
    </Link>
  );
}

function WaitingCard({ to, icon: Icon, count, title, blurb, accent, testid }) {
  const hasItems = (count ?? 0) > 0;
  return (
    <Link to={to} className="h-card p-5 flex items-start gap-4 hover:shadow-seal transition-shadow" data-testid={testid}>
      <span className={`w-12 h-12 rounded-full ${accent && hasItems ? "bg-terracotta" : "bg-sage"} text-cream-soft flex items-center justify-center shrink-0`}>
        {hasItems ? <Icon size={20} /> : <CheckCircle2 size={20} />}
      </span>
      <span className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <div className="font-display text-2xl text-sage-dark">{title}</div>
          {hasItems && (
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${accent ? "bg-terracotta text-cream-soft" : "bg-sage text-cream-soft"}`}>
              {count} waiting
            </span>
          )}
          {!hasItems && (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-cream-deep text-ink-soft">
              All caught up
            </span>
          )}
        </div>
        <div className="text-sm text-ink-soft mt-1">{blurb}</div>
      </span>
      <ChevronRight size={18} className="text-sage-dark shrink-0 self-center" />
    </Link>
  );
}

function ToolLink({ to, icon: Icon, label, blurb, testid }) {
  return (
    <Link to={to} className="h-card p-4 flex items-center gap-3 hover:shadow-seal transition-shadow" data-testid={testid}>
      <span className="w-11 h-11 rounded-full bg-ink text-cream-soft flex items-center justify-center shrink-0">
        <Icon size={20} />
      </span>
      <span className="flex-1 min-w-0">
        <div className="font-display text-xl text-sage-dark">{label}</div>
        <div className="text-sm text-ink-soft">{blurb}</div>
      </span>
      <ChevronRight size={16} className="text-sage-dark shrink-0" />
    </Link>
  );
}
